import { useCallback } from 'react';

import { useFocusEffect } from 'expo-router';
import { useCartContinue } from 'features/cart/hooks/use-cart-continue';
import { useCartLiveValidationActions } from 'features/cart/hooks/use-cart-live-validation-actions';
import { iosCartBoldWeight } from 'features/cart/styles';
import { calculateCartSubTotalWithEstimatedFees } from 'features/cart/utils';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';

import { LegacyCondimentsOnlyModal } from '../../../../pages/cart/legacy-condiments-only-modal';
import { CartFooterContinueButton } from '../cart-actions/cart-footer-continue-button';
import { CartDeliveryMinimumSubtotalModal } from '../cart-modals/cart-delivery-minimum-subtotal-modal';
import { CartInvalidItemsModal } from '../cart-modals/cart-invalid-items-modal';
import { CartPricingErrorModal } from '../cart-modals/cart-pricing-error-modal';
import { CartSelectionRequiredModal } from '../cart-modals/cart-selection-required-modal';

import { CartFooterDiscountOffer } from './cart-footer-discount-offer';

export const CartFooter = () => {
  const {
    cartEntries,
    handleOpenInvalidItemsModal,
    handleContinue,
    handleCondimentsOnlyClose,
    handleDeliveryMinimumSubtotalClose,
    handleInvalidItemsConfirm,
    handlePricingErrorClose,
    handleSelectionRequiredClose,
    handleTryAgain,
    handleTryAnotherStore,
    deliveryMinimumSubtotalCents,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    isContinueDisabled,
    isPricingLoading,
    pricingError,
    showDeliveryMinimumSubtotalModal,
    showCondimentsOnlyModal,
    showInvalidItemsModal,
    showSelectionRequiredModal,
  } = useCartContinue();
  const { validateCartNow } = useCartLiveValidationActions();
  const { isDelivery } = useServiceModeContext();
  const { deliveryAddress } = useOrderContext();
  const subTotal = calculateCartSubTotalWithEstimatedFees({
    cartEntries,
    isDelivery,
    dropoffState: deliveryAddress?.state ?? null,
  });
  const { formatCurrencyForLocale } = useUIContext();

  useFocusEffect(
    useCallback(() => {
      validateCartNow();
      handleOpenInvalidItemsModal();
    }, [handleOpenInvalidItemsModal, validateCartNow])
  );

  return (
    <View style={styles.footer}>
      <CartFooterDiscountOffer />
      <Typography style={styles.text} testID="cart-footer-subtotal">
        Subtotal: {formatCurrencyForLocale(subTotal)}
      </Typography>
      <CartFooterContinueButton
        disabled={isContinueDisabled}
        isLoading={isPricingLoading}
        onPress={handleContinue}
      />
      <CartDeliveryMinimumSubtotalModal
        isOpen={showDeliveryMinimumSubtotalModal}
        onClose={handleDeliveryMinimumSubtotalClose}
        minimumSubtotalCents={deliveryMinimumSubtotalCents}
      />
      <CartInvalidItemsModal
        isOpen={showInvalidItemsModal}
        cartEntries={invalidCartEntries}
        offers={invalidOfferEntries}
        rewards={invalidRewardEntries}
        onConfirm={handleInvalidItemsConfirm}
      />
      <CartSelectionRequiredModal
        isOpen={showSelectionRequiredModal}
        onClose={handleSelectionRequiredClose}
      />
      <LegacyCondimentsOnlyModal
        isOpen={showCondimentsOnlyModal}
        onClose={handleCondimentsOnlyClose}
      />
      <CartPricingErrorModal
        isOpen={pricingError}
        onClose={handlePricingErrorClose}
        onTryAgain={handleTryAgain}
        onTryAnotherStore={handleTryAnotherStore}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  footer: {
    width: '100%',
    flexDirection: 'column',
    gap: 8,
  },
  text: {
    alignSelf: 'center',
    fontSize: 19,
    fontFamily: primitive.fontFamily.$headers,
    ...iosCartBoldWeight,
    color: primitive.$bbqBrown,
  },
});
